import { Button, Modal, LegacyStack, ChoiceList } from '@shopify/polaris';
import axios, { AxiosError } from 'axios';
import saveAs from 'file-saver';
import React, { useCallback, useEffect, useState } from 'react';

import { parseDateLabel } from '../../utils/Common';

interface Props {
  active: boolean;
  handleModalChange: () => void;
  organizationId: string;
  toggleToastSuccess: () => void;
  toggleBannerError: (value: boolean) => void;
  startDate: Date;
  endDate: Date;
}

type ReportChoice = {
  label: string;
  value: string;
};

export function PrintReportModal(props: Props) {
  const [buttonSpinning, setButtonSpinning] = useState(false);
  const [reportOptions, setReportOptions] = useState<ReportChoice[]>([{ label: 'Nessun report disponibile', value: 'no_report' }]);
  const [hasReports, setHasReports] = useState(false);
  const [selectedReport, setSelectedReport] = useState<string[]>([]);
  const [selectedExportAs, setSelectedExportAs] = useState<string[]>(['excel']);

  const exportAsOptions = [{ label: 'Excel', value: 'excel' }];

  const handleSelectedReport = useCallback((value: string[]) => setSelectedReport(value), []);
  const handleSelectedExportAs = useCallback((value: string[]) => setSelectedExportAs(value), []);

  const handleClose = () => {
    props.handleModalChange();
    setSelectedReport([]);
    setSelectedExportAs(['excel']);
  };

  /**
   * Fetch available reports for this organization
   */
  useEffect(() => {
    const fetchReports = async () => {
      try {
        const token = localStorage.getItem('mb__access_token');

        if (!props.organizationId) {
          setReportOptions([{ label: 'No organization associated', value: 'no_organization' }]);
          setHasReports(false);
          return;
        }

        const response = await axios.get(`${process.env.REACT_APP_API_URL || '/api'}/admin/organizations/${props.organizationId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = response.data;
        if (data.status === 'success') {
          const { organization } = data.data;
          const tmpReportOptions: ReportChoice[] = [];

          if (organization && organization.products) {
            for (const product of organization.products) {
              if (product.reports && product.reports.length > 0) {
                for (const report of product.reports) {
                  if (!tmpReportOptions.some((option) => option.value === report._id)) {
                    tmpReportOptions.push({ label: report.name, value: report._id });
                  }
                }
              }
            }
            // if (product.reports && !tmpReportOptions.some((option) => option.value === product.report._id)) {
            //   tmpReportOptions.push({ label: product.report.name, value: product.report._id });
            // }
          }

          if (tmpReportOptions.length > 0) {
            setReportOptions(tmpReportOptions);
            setHasReports(true);
          } else {
            setReportOptions([{ label: 'No report available', value: 'no_report' }]);
            setHasReports(false);
          }
        } else {
          setReportOptions([{ label: 'Error during report loading', value: 'error' }]);
          setHasReports(false);
        }
      } catch (error) {
        const axiosError = error as AxiosError;
        setReportOptions([{ label: 'Error during reports loading', value: 'error' }]);
        setHasReports(false);
        props.toggleBannerError(true);
      }
    };
    fetchReports();
  }, [props.organizationId, props.toggleBannerError]);

  /**
   * Handle print report
   */
  const handlePrintReport = useCallback(async () => {
    if (!selectedReport[0] || selectedReport[0] === 'no_report' || selectedReport[0] === 'no_organization' || selectedReport[0] === 'error') {
      props.toggleBannerError(true);
      return;
    }
    try {
      setButtonSpinning(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || '/api'}/admin/reports/print`,
        {
          report_id: selectedReport[0],
          export_as: selectedExportAs[0],
          date_start: props.startDate.toISOString(),
          date_end: props.endDate.toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('mb__access_token')}`,
          },
        },
      );
      const fileName = selectedReport[0].includes('itas')
        ? `ITAS_Active_Report_${parseDateLabel(props.startDate)}.xlsx`
        : `report_${parseDateLabel(props.startDate)}.xlsx`;
      saveAs(response.data, fileName);
      handleClose();
      props.toggleToastSuccess();
    } catch (error) {
      const axiosError = error as AxiosError;
      props.toggleBannerError(true);
    } finally {
      setButtonSpinning(false);
    }
  }, [selectedReport, selectedExportAs, props.startDate, props.endDate, handleClose, props.toggleToastSuccess, props.toggleBannerError]);

  return (
    <Modal
      open={props.active}
      onClose={handleClose}
      title="Genera report"
      primaryAction={{
        content: 'Stampa',
        onAction: handlePrintReport,
        loading: buttonSpinning,
        disabled: !hasReports || selectedReport[0] === 'no_report' || selectedReport[0] === 'no_organization' || selectedReport[0] === 'error',
      }}
      secondaryActions={[
        {
          content: 'Cancella',
          onAction: handleClose,
        },
      ]}
    >
      <Modal.Section>
        <LegacyStack vertical>
          <p>Puoi selezionare uno tra i seguenti report attivi per la tua organizzazione, per il periodo indicato.</p>
          {hasReports ? (
            <LegacyStack.Item>
              <ChoiceList title="Report" choices={reportOptions} selected={selectedReport} onChange={handleSelectedReport} />
            </LegacyStack.Item>
          ) : (
            <p>Nessun report disponibile.</p>
          )}
          {/* Export */}
          {/* <LegacyStack.Item>
            <ChoiceList title="Esporta" choices={exportOptions} selected={selectedExport} onChange={handleSelectedExport} />
          </LegacyStack.Item> */}
          {/* Export as */}
          <LegacyStack.Item>
            <ChoiceList title="Esporta come" choices={exportAsOptions} selected={selectedExportAs} onChange={handleSelectedExportAs} />
          </LegacyStack.Item>
        </LegacyStack>
      </Modal.Section>
    </Modal>
  );
}
